import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { PathSegments } from '../../abstract/path-segments';
import { DjiDraftPreview } from '../response-models/dji-draft-preview.model';
import { DjiDroneStatus } from '../response-models/dji-drone-status.model';
import { DjiFlight } from '../data-models/dji-flight.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';
import { alreadyLinkedSprayRecordId, withFallbackMessage } from '../utils/dji-error-utils';

/**
 * DJI flight data for the current organization, and what can be done with it (ADN-2307): `/dji/spray-records/:id`,
 * `/dji/drones/:droneId` and `/dji/flights/:intakeId/*` (hub `user-dji-controller.ts`).
 *
 * Every call is organization-scoped on the hub; a record, drone or flight that is not the caller's is an error, not
 * empty data. Failures reject with an `ApiError` carrying the hub's own message (or a per-call fallback). A flight
 * that already has a record answers 409 `FLIGHT_ALREADY_LINKED` with that record in `detail.sprayRecordId`.
 */
@Injectable({
  providedIn: 'root'
})
export class DjiService extends ApiService {
  constructor() {
    super('dji');
  }

  /** DJI flights linked to a spray record, earliest first. Empty when none are linked. */
  async getSprayRecordFlights(sprayRecordId: string): Promise<DjiFlight[]> {
    const read = (data: unknown) => (Array.isArray(data) ? (data as DjiFlight[]) : null);
    return this.run('GET', ['spray-records', sprayRecordId], 'Could not load DJI flight data for this record.', read);
  }

  /** Whether ADN is receiving DJI flights for a drone. */
  async getDroneStatus(droneId: string): Promise<DjiDroneStatus> {
    const read = (data: unknown) => (isRecord(data) ? (data as unknown as DjiDroneStatus) : null);
    return this.run('GET', ['drones', droneId], 'Could not load DJI status for this drone.', read);
  }

  /**
   * What starting a spray record from this flight would create, without creating it. `alreadyLinked` carries
   * the record the flight already has, in which case there is nothing to preview.
   */
  async previewSprayRecordFromFlight(
    intakeId: string
  ): Promise<{ draft: DjiDraftPreview } | { alreadyLinked: string }> {
    try {
      const read = (data: unknown) => (isRecord(data) ? (data as unknown as DjiDraftPreview) : null);
      const draft = await this.run('GET', ['flights', intakeId, 'spray-record-preview'], 'Could not preview the spray record.', read);
      return { draft };
    } catch (error: unknown) {
      const existing = alreadyLinkedSprayRecordId(error);
      if (existing) {
        return { alreadyLinked: existing };
      }
      throw error;
    }
  }

  /**
   * Starts a draft spray record from a DJI flight and links the flight to it. Resolves with the record to open;
   * `alreadyLinked` is true when the flight already had one, which is returned instead of creating a second.
   */
  async createSprayRecordFromFlight(intakeId: string): Promise<{ sprayRecordId: string; alreadyLinked: boolean }> {
    try {
      const path = ['flights', intakeId, 'create-spray-record'];
      const sprayRecordId = await this.run('POST', path, 'Could not create the spray record.', toSprayRecordId);
      return { sprayRecordId, alreadyLinked: false };
    } catch (error: unknown) {
      const existing = alreadyLinkedSprayRecordId(error);
      if (existing) {
        return { sprayRecordId: existing, alreadyLinked: true };
      }
      throw error;
    }
  }

  /** Sets a flight aside (a test or practice flight, not a real job). */
  async dismissFlight(intakeId: string): Promise<void> {
    await this.run('POST', ['flights', intakeId, 'dismiss'], 'Could not dismiss this flight.', () => undefined);
  }

  /** Takes a dismissal back. */
  async restoreFlight(intakeId: string): Promise<void> {
    await this.run('POST', ['flights', intakeId, 'restore'], 'Could not restore this flight.', () => undefined);
  }

  /**
   * One request. `read` returns `null` when the response lacks the expected data, which rejects like a failure.
   * Every failure rejects with an `ApiError` (the hub's message, else `fallback`).
   */
  private async run<R>(method: 'GET' | 'POST', path: PathSegments, fallback: string, read: (data: unknown) => R | null): Promise<R> {
    const body = method === 'POST' ? {} : undefined;
    try {
      const result = await firstValueFrom(this.request(method, { path }, read, body));
      if (result === null) {
        throw new Error('missing data');
      }
      return result;
    } catch (error: unknown) {
      throw withFallbackMessage(error, fallback);
    }
  }
}

/** The created record's id; `null` (a hub fault) when the response has none. */
function toSprayRecordId(data: unknown): string | null {
  const id = isRecord(data) ? nullableString(data['sprayRecordId']) : undefined;
  return id ? id : null;
}
